/**
 * بیمه — بخشِ مستقلِ حسابداری.
 *
 * این لیست جدا از محاسبهٔ حقوقِ خودِ برنامه است: مبلغی که اینجا ثبت می‌شود
 * (چه از فایلِ سازمان، چه دستی) عیناً در کسوراتِ همان دوره می‌نشیند و روی فیش
 * حقوقی چاپ می‌شود — جای فرمولِ درصدی را می‌گیرد.
 *
 * دو راهِ ورود، طبقِ خواستهٔ کاربر:
 *   ۱) بارگذاریِ فایل اکسل، با پیش‌نمایش پیش از ثبت
 *   ۲) افزودن و ویرایشِ دستیِ هر ردیف — مثل بقیهٔ لیست‌های برنامه
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Button, Space, Select, Upload, message, Modal, Tag, Row, Col, Statistic, Input, Form, Checkbox, Empty, Segmented, Dropdown } from "antd";
import Alert from "../components/GxAlert";
import {
  UploadOutlined, PlusOutlined, FileExcelOutlined, ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import GxTable from "../components/GxTable";
import GxTabs from "../components/GxTabs";

const { Text } = Typography;
import EditableTable from "../components/EditableTable";
import { formatMoney, MoneyInput } from "../utils/money";
import { JALALI_MONTHS, currentJalali, fmtNum, toJalali } from "../utils/dateUtils";
import { filterByChildren } from "../utils/selectFilter";
import { useAuth } from "../context/AuthContext";
import { saveResponse } from "../utils/downloadFile";
import InsuranceListModal from "../components/InsuranceListModal";
import EmptyWorkArea from "../components/EmptyWorkArea";
import { apiError } from "../utils/apiError";
import PayrollExplainDrawer from "../components/PayrollExplainDrawer";
import { getActiveYm, setActiveYm } from "../utils/activeMonth";
import {
  getPeriods, createPeriod, getEmployees,
  importInsurance, getInsuranceRecords, getInsuranceSummary,
  createInsuranceRecord, updateInsuranceRecord, deleteInsuranceRecord,
  bulkUpdateInsurance, getInsurancePayrollView, exportInsurance, downloadFile,
} from "../api/client";

const money = (v) => formatMoney(v);

export default function InsurancePage() {
  const { can } = useAuth();
  const canEdit = can("accounting.edit");

  const [periods, setPeriods] = useState([]);
  // ماه مستقل از «دورهٔ موجود» انتخاب می‌شود؛ دوره تنبل ساخته می‌شود.
  // 🐞 قبلاً فقط دوره‌های ثبت‌شده در Select می‌آمدند و چون روی سرور یک دوره بیشتر
  // نبود، عملاً ماه جابه‌جا نمی‌شد.
  /* ?year=&month= از لینکِ صفحهٔ حقوق می‌آید. بدونِ این، کلیک روی «حق بیمه»ی
     مرداد صفحه را روی ماهِ جاری باز می‌کرد و کاربر می‌دید «ماهِ اشتباه آورد». */
  const [ym, setYm] = useState(() => {
    const c = currentJalali();
    // `window` در رندرِ سمتِ سرورِ تستِ دود وجود ندارد
    const qs = typeof window !== "undefined"
      ? new URLSearchParams(window.location.search) : new URLSearchParams("");
    const qy = Number(qs.get("year"));
    const qm = Number(qs.get("month"));
    const a = getActiveYm() || {};           // ماهِ فعالِ مشترک (v2.132.0)
    return {
      year: qy > 1300 ? qy : (a.year || c.year),
      month: qm >= 1 && qm <= 12 ? qm : (a.month || c.month),
    };
  });
  useEffect(() => { setActiveYm(ym.year, ym.month); }, [ym.year, ym.month]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [payrollView, setPayrollView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  /** دورهٔ همین ماه، اگر ساخته شده باشد. */
  const period = useMemo(
    () => periods.find((p) => p.year === ym.year && p.month === ym.month) || null,
    [periods, ym]);
  const periodId = period?.id || null;

  const [preview, setPreview] = useState(null);   // نتیجهٔ پیش‌نمایشِ فایل
  const [pendingFile, setPendingFile] = useState(null);
  const [replace, setReplace] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  // کلیک روی هر ردیف ⇒ «چرا حقوقش این‌قدر شد؟»
  const [explainFor, setExplainFor] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    getPeriods().then((r) => setPeriods(r.data || []));
    getEmployees().then((r) => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!periodId) { setRows([]); setSummary(null); setPayrollView(null); return; }
    setLoading(true);
    Promise.all([
      getInsuranceRecords({ period_id: periodId }),
      getInsuranceSummary(periodId),
      getInsurancePayrollView(periodId),
    ])
      .then(([a, b, c]) => { setRows(a.data || []); setSummary(b.data); setPayrollView(c.data); })
      .catch(() => message.error("خطا در دریافت اطلاعات بیمه"))
      .finally(() => setLoading(false));
  }, [periodId]);

  useEffect(() => { load(); }, [load]);

  const empOptions = useMemo(
    () => employees.map((e) => ({ value: e.id, label: `${e.full_name} (${e.employee_code || "—"})` })),
    [employees]);
  // v2.166.0 (کاربر): «در سرچِ افزودنِ دستی فقط کسانی که بیمه دارند بیایند»
  const insuredOptions = useMemo(
    () => employees.filter((e) => e.has_insurance && e.status === "active")
      .map((e) => ({ value: e.id, label: `${e.full_name} (${e.employee_code || "—"})` })),
    [employees]);

  // ─────────────── بارگذاریِ فایل: اول پیش‌نمایش ───────────────
  /** مرحلهٔ ایمپورت: ۱) حق بیمه  ۲) پرداختیِ بیمه */
  const [stage, setStage] = useState("premium");
  const [overrides, setOverrides] = useState({});   // v2.163.0: {row: employee_id} — تطبیقِ دستی در پیش‌نمایش

  const doPreview = (file, stageArg) => {
    const st = typeof stageArg === "string" ? stageArg : stage;
    setLoading(true);
    // دوره تنبل ساخته می‌شود: تا وقتی کاربر واقعاً فایلی نداده، ماهِ خالی
    // دوره‌ای در دیتابیس نمی‌سازد.
    ensurePeriod()
      .then((pid) => importInsurance(pid, file, { preview: true, stage: st }))
      .then((r) => { setPreview(r.data); setPendingFile(file); setOverrides({}); })
      .catch((e) => message.error(apiError(e, "فایل خوانده نشد")))
      .finally(() => setLoading(false));
    return false;   // جلوگیری از آپلودِ خودکارِ antd
  };

  const applyImport = () => {
    setLoading(true);
    ensurePeriod()
      .then((pid) => importInsurance(pid, pendingFile, { preview: false, replace, stage, overrides }))
      .then((r) => {
        message.success(r.data.message);
        setPreview(null); setPendingFile(null); setOverrides({}); load();
      })
      .catch((e) => message.error(apiError(e, "ثبت ناموفق بود")))
      .finally(() => setLoading(false));
  };

  /* v2.164.0 (کاربر): «سه لیستِ انحصاریِ بیمه‌ای در بخشِ بیمه — کارمزدی، غیرکارمزدی، و هر دو — با همان انتخاب‌ها (حساب، پیش‌فرض)؛
     یک نسخه هم که نفراتِ پرداخت‌شده در آن نیایند» — همان خروجیِ صفحهٔ محاسبهٔ حقوق (`/export/insurance`). */
  const [insList, setInsList] = useState(null);       // group: piece | attend | "" (همه)
  const runInsList = async (group, opt) => {
    try {
      const name = await downloadFile("/api/v1/export/insurance", "لیست بیمه.xlsx", { params: {
        period_id: periodId, accounts: opt.accounts, unpaid_only: opt.unpaid_only, ...(group ? { group } : {}) } });
      message.success(`«${name}» دانلود شد`);
    } catch (e) { message.error(apiError(e, "لیست ساخته نشد — حقوقِ این ماه محاسبه شده؟")); }
  };

  const doExport = () => {
    exportInsurance(periodId)
      .then((res) => saveResponse(res, `insurance-${periodId}.xlsx`))
      .catch(() => message.error("خطا در دریافت خروجی اکسل"));
  };

  // ─────────────── ستون‌های لیستِ قابلِ ویرایش ───────────────
  const cols = [
    {
      title: "نام (طبق فایل)", dataIndex: "employee_name_raw", editType: "text",
      render: (v, r) => (r.employee_id ? (
        <a onClick={(e) => { e.stopPropagation(); setExplainFor(r.employee_id); }}>{v}</a>
      ) : v),
      sorter: (a, b) => String(a.employee_name_raw || "").localeCompare(String(b.employee_name_raw || "")),
    },
    {
      title: "پرسنلِ تطبیق‌یافته", dataIndex: "employee_id", editType: "select",
      options: empOptions, showSearch: true, filterOption: filterByChildren,
      sorter: (a, b) => String(a.employee_name || "").localeCompare(String(b.employee_name || "")),
      filters: [{ value: "matched", text: "تطبیق‌یافته" },
        { value: "unmatched", text: "بدون تطبیق" }],
      onFilter: (v, r) => r.matched_status === v,
      render: (_, r) => (r.employee_name
        ? <span>{r.employee_name} <Tag color="green">{r.employee_code}</Tag></span>
        : <Tag color="orange" icon={<WarningOutlined />}>بدون تطبیق</Tag>),
    },
    { title: "کد ملی", dataIndex: "national_id_raw", editType: "text",
      sorter: (a, b) => String(a.national_id_raw || "").localeCompare(String(b.national_id_raw || "")) },
    {
      title: "حق بیمه سهم کارمند (ریال)", dataIndex: "insurance_premium", editType: "number",
      align: "left", render: money,
      sorter: (a, b) => (a.insurance_premium || 0) - (b.insurance_premium || 0),
    },
    {
      title: "جمع کسر از حقوق (ریال)", dataIndex: "total_insurance_deduction",
      align: "left", render: (v) => <b>{money(v)}</b>,
      sorter: (a, b) => (a.total_insurance_deduction || 0) - (b.total_insurance_deduction || 0),
    },
    { title: "توضیح", dataIndex: "notes", editType: "text" },
  ];

  /** یک ماه جلو/عقب — حتی اگر دوره‌اش هنوز ساخته نشده باشد. */
  const stepMonth = (d) => setYm(({ year, month }) => {
    const t = year * 12 + (month - 1) + d;
    return { year: Math.floor(t / 12), month: (t % 12) + 1 };
  });

  const yearOptions = useMemo(() => {
    const c = currentJalali().year;
    const years = new Set([c, c - 1, c + 1, ym.year, ...periods.map((p) => p.year)]);
    return [...years].sort((a, b) => b - a).map((y) => ({ value: y, label: fmtNum(y) }));
  }, [periods, ym.year]);

  /** ماه‌هایی که داده دارند نشانه می‌گیرند تا کاربر بداند کجا پر است. */
  const monthsWithData = useMemo(
    () => new Set(periods.filter((p) => p.year === ym.year).map((p) => p.month)),
    [periods, ym.year]);

  const periodSelector = (
    <Space.Compact>
      <Button onClick={() => stepMonth(-1)} title="ماه قبل">‹</Button>
      <Select value={ym.month} style={{ width: 130 }}
        onChange={(m) => setYm((v) => ({ ...v, month: m }))}
        options={JALALI_MONTHS.map((label, i) => ({
          value: i + 1,
          label: monthsWithData.has(i + 1) ? `${label} •` : label,
        }))} />
      <Select value={ym.year} style={{ width: 100 }} options={yearOptions}
        onChange={(y) => setYm((v) => ({ ...v, year: y }))} />
      <Button onClick={() => stepMonth(1)} title="ماه بعد">›</Button>
    </Space.Compact>
  );

  const periodLabel = `${JALALI_MONTHS[ym.month - 1]} ${fmtNum(ym.year)}`;

  /** دورهٔ این ماه را در صورتِ نبود می‌سازد — فقط لحظه‌ای که واقعاً چیزی ثبت می‌شود. */
  const ensurePeriod = async () => {
    if (periodId) return periodId;
    const r = await createPeriod({ year: ym.year, month: ym.month });
    const list = (await getPeriods()).data || [];
    setPeriods(list);
    return r.data.id;
  };

  const stats = summary && (
    <Row gutter={[12, 12]} style={{ marginBottom: 12 }} key={`${periodId}-${summary.count}`}>
      <Col xs={12} md={8} lg={4}><Card size="small">
        <Statistic title="تعداد بیمه‌شده" value={summary.count} /></Card></Col>
      <Col xs={12} md={6}><Card size="small">
        <Statistic title="پرداختی به سازمان بیمه (ریال)"
          value={money(summary.payable_to_insurance)}
          valueStyle={{ color: "#1F4E79" }} />
        <Text type="secondary" style={{ fontSize: 11 }}>
          جمعِ حق بیمهٔ مرحلهٔ ۱ — همین مبلغ را به بیمه می‌پردازید
        </Text></Card></Col>
      <Col xs={12} md={8} lg={6}><Card size="small">
        <Statistic title="جمع کسر بیمه (ریال)" value={money(summary.total_deduction)} /></Card></Col>
      {/* «خالص پرداختی»ِ فایل — کسور نیست، برای مقایسه با حقوق است */}
      <Col xs={12} md={8} lg={6}><Card size="small">
        <Statistic title="جمع خالص پرداختی (ریال)" value={money(summary.total_net_payable)}
          valueStyle={{ color: "#1F4E79" }} /></Card></Col>
      <Col xs={12} md={8} lg={2}><Card size="small">
        <Statistic title="تطبیق‌یافته" value={summary.matched}
          valueStyle={{ color: "#3f8600" }} /></Card></Col>
      <Col xs={12} md={8} lg={3}><Card size="small">
        <Statistic title="بدون تطبیق" value={summary.unmatched}
          valueStyle={{ color: summary.unmatched ? "#cf1322" : undefined }} /></Card></Col>
    </Row>
  );

  /** ماهی که هنوز چیزی برایش ثبت نشده — به‌جای جدولِ خالی، راه را نشان بده. */
  const emptyMonth = (
    <EmptyWorkArea
      message={canEdit
        ? `برای ${periodLabel} هنوز بیمه‌ای ثبت نشده — فایلِ سازمان را بارگذاری کنید (xlsx، xls، xml، csv یا PDFِ متن‌دار) یا ردیف‌ها را دستی وارد کنید.`
        : `برای ${periodLabel} هنوز بیمه‌ای ثبت نشده است.`}
      action={canEdit ? (
        <Space wrap>
          <Upload beforeUpload={doPreview} showUploadList={false}
            accept=".xlsx,.xls,.csv,.xml,.pdf">
            <Button type="primary" icon={<UploadOutlined />}>بارگذاری فایل بیمه</Button>
          </Upload>
          <Button icon={<PlusOutlined />}
            onClick={() => { form.resetFields(); setAddOpen(true); }}>افزودن دستی</Button>
        </Space>
      ) : null} />
  );

  /** «خالص پرداختی» — مبلغی که کارخانه از کانالِ بیمه پرداخت کرده.
   *  کنارِ «حق بیمه» در جمعِ کسر می‌آید و از حقوقِ همان ماه کم می‌شود. */
  const payableCol = {
    title: "خالص پرداختی (ریال)", dataIndex: "net_payable", width: 170, align: "left",
    editType: "number",
    sorter: (a, b) => (a.net_payable || 0) - (b.net_payable || 0),
    render: (v) => (v ? <span className="gx-money">{money(v)}</span>
      : <span style={{ color: "#ccc" }}>—</span>),
  };

  /** ستون‌های نمایشیِ منبع — تا معلوم باشد این ردیف از کدام فایل و کِی آمده. */
  const sourceCols = [
    {
      title: "منبع", dataIndex: "source_file", width: 150,
      render: (v) => (v ? <Tag>{v}</Tag> : <span style={{ color: "#ccc" }}>—</span>),
      sorter: (a, b) => String(a.source_file || "").localeCompare(String(b.source_file || "")),
    },
    {
      title: "زمانِ ورود", dataIndex: "imported_at", width: 120,
      render: (v) => (v ? toJalali(String(v).slice(0, 10)) : "—"),
      sorter: (a, b) => String(a.imported_at || "").localeCompare(String(b.imported_at || "")),
    },
  ];

  const listTab = (!loading && rows.length === 0) ? emptyMonth : (
    <>
      {stats}
      {summary?.unmatched > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`${summary.unmatched} ردیف به هیچ پرسنلی وصل نشده و کسر نخواهد شد`}
          description="ستون «پرسنلِ تطبیق‌یافته» را ویرایش کنید تا مبلغ در فیش حقوقی اعمال شود." />
      )}
      {/* جمعِ ته هر ستون — همان الگوی جدول‌های زنجیره تأمین */}
      <EditableTable
        layoutId="insurance-records" dataSource={rows} rowKey="id"
        columns={[...cols, payableCol, ...sourceCols]}
        gxSummary={[
          { dataIndex: "employee_name_raw", type: "unique" },
          { dataIndex: "insurance_premium", type: "sum", format: money },
          { dataIndex: "total_insurance_deduction", type: "sum", format: money },
          { dataIndex: "net_payable", type: "sum", format: money },
        ]}
        onRow={(r) => ({
          onClick: () => r.employee_id && setExplainFor(r.employee_id),
          style: r.employee_id ? { cursor: "pointer" } : undefined,
        })}
        loading={loading} pagination={{ pageSize: 25 }}
        onSave={canEdit ? (id, fields) => updateInsuranceRecord(id, fields).then(load) : undefined}
        onSaveMany={canEdit ? (changes) => bulkUpdateInsurance(
          Object.entries(changes).map(([id, f]) => ({ id: Number(id), ...f }))).then(load) : undefined}
        onDelete={canEdit ? (id) => deleteInsuranceRecord(id).then(load) : undefined}
      />
    </>
  );

  const pvCols = [
    { title: "کد", dataIndex: "employee_code", width: 90 },
    { title: "نام", dataIndex: "name" },
    { title: "بخش", dataIndex: "department" },
    { title: "کسر بیمه (ریال)", dataIndex: "insurance_deduction", align: "left", render: money },
    {
      title: "ناخالص حقوق (ریال)", dataIndex: "gross", align: "left",
      render: (v, r) => (r.has_payroll ? money(v) : <Tag>حقوق محاسبه نشده</Tag>),
    },
    {
      title: "خالص پرداختی (ریال)", dataIndex: "net_pay", align: "left",
      render: (v, r) => (r.has_payroll ? <b>{money(v)}</b> : "—"),
    },
    { title: "بانک", dataIndex: "bank_name" },
    { title: "شماره حساب", dataIndex: "bank_account" },
    { title: "شبا", dataIndex: "iban" },
  ];

  const accountantTab = payrollView && (
    <>
      <Row gutter={12} style={{ marginBottom: 12 }} key={`pv-${periodId}-${payrollView.totals.count}`}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="جمع کسر بیمه (ریال)" value={money(payrollView.totals.deduction)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="جمع خالص پرداختی (ریال)" value={money(payrollView.totals.net_pay)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="بدون فیش حقوقی" value={payrollView.totals.without_payroll} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="بدون تطبیق" value={payrollView.totals.unmatched} /></Card></Col>
      </Row>
      <GxTable layoutId="insurance-payroll-view" dataSource={payrollView.rows} columns={pvCols}
        rowKey="id" size="small" loading={loading} pagination={{ pageSize: 25 }} />
    </>
  );

  return (
    <div>
      <Card
        title={`بیمه — ${periodLabel}`}
        extra={
          <Space wrap>
            {periodSelector}
            <Button icon={<ReloadOutlined />} onClick={load}>بازخوانی</Button>
            <Button icon={<FileExcelOutlined />} onClick={doExport}>خروجی اکسل</Button>
            <Dropdown disabled={!periodId} trigger={["click"]} menu={{
              items: [{ key: "attend", label: "لیست بیمه — غیرکارمزدی" },
                      { key: "piece", label: "لیست بیمه — کارمزدی" },
                      { key: "all", label: "لیست بیمه — همه (هر دو)" }],
              onClick: ({ key }) => setInsList({ group: key === "all" ? "" : key }),
            }}>
              <Button type="primary" ghost icon={<FileExcelOutlined />}>لیست‌های بیمه ▾</Button>
            </Dropdown>
            <InsuranceListModal open={!!insList}
              title={`لیست بیمه — ${insList?.group === "piece" ? "کارمزدی" : insList?.group === "attend" ? "غیرکارمزدی" : "همه"}`}
              onCancel={() => setInsList(null)}
              onOk={(opt) => { const g = insList.group; setInsList(null); runInsList(g, opt); }} />
            {canEdit && (
              <Space wrap size={6}>
                {/* دو مرحلهٔ بیمه — انتخابِ صریح، چون فایلِ هر مرحله فرق دارد
                    و مرحلهٔ ۲ نباید حق بیمهٔ مرحلهٔ ۱ را پاک کند. گوشی: Space با wrap به‌جای Compact (v2.132.0) */}
                <Segmented value={stage} onChange={setStage} options={[
                  { value: "premium", label: "۱) حق بیمه" },
                  { value: "payment", label: "۲) پرداختیِ بیمه" },
                ]} />
                <Upload beforeUpload={doPreview} showUploadList={false}
                  accept=".xlsx,.xls,.csv,.xml,.pdf">
                  <Button icon={<UploadOutlined />}>
                    بارگذاری فایلِ {stage === "premium" ? "حق بیمه" : "پرداختی"}
                  </Button>
                </Upload>
              </Space>
            )}
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />}
                onClick={() => { form.resetFields(); setAddOpen(true); }}>افزودن دستی</Button>
            )}
          </Space>
        }
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="«کسر بیمه» = فقط حق بیمه — «پرداختی» = پرداختِ حقوق"
          description="حق بیمهٔ سهمِ کارمند از حقوق کسر می‌شود (اگر آن را عوض کنید، حقوقِ دوره باید دوباره محاسبه شود). «پرداختیِ بیمه» (واریز از بانکِ رسمی) کسر نیست — خودکار در «پرداخت حقوق» به‌نامِ «بیمه — بانکِ رسمی» ثبت می‌شود، از ماندهٔ پرداخت کم می‌شود و در «جزئیات پرداخت»ِ فیش می‌آید؛ نیازی به بازکردن یا محاسبهٔ مجدد نیست." />

        <GxTabs urlKey="tab" items={[
          { key: "list", label: "لیست بیمه", children: listTab },
          { key: "accounting", label: "بیمه در کنارِ حقوق و حساب‌ها", children: accountantTab || <Empty /> },
        ]} />
      </Card>

      <PayrollExplainDrawer periodId={periodId} employeeId={explainFor}
        onClose={() => setExplainFor(null)} />

      {/* پیش‌نمایشِ فایل — پیش از ثبت */}
      <Modal open={!!preview} width={900} title="پیش‌نمایش فایل بیمه"
        onCancel={() => { setPreview(null); setPendingFile(null); }}
        onOk={applyImport} okText="ثبت نهایی" cancelText="انصراف"
        okButtonProps={{ disabled: !preview?.rows }}>
        {preview && (
          <>
            {preview.month_mismatch && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                icon={<WarningOutlined />}
                message="ماهِ فایل با ماهِ انتخاب‌شده نمی‌خواند"
                description={preview.month_mismatch} />
            )}
            <Alert type={preview.warning ? "warning" : "info"} showIcon style={{ marginBottom: 12 }}
              message={<>ثبت در <b>{periodLabel}</b> — {preview.message}</>}
              description={preview.warning} />
            {preview.suggest_payment_stage && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message="این فایل احتمالاً فایلِ «پرداختی» است، نه حق بیمه"
                description={<>
                  {preview.suggest_payment_stage}{" "}
                  <Button size="small" type="primary" onClick={() => { setStage("payment"); doPreview(pendingFile, "payment"); }}>
                    ثبت به‌عنوانِ پرداختی (مرحلهٔ ۲)
                  </Button>
                </>} />
            )}
            {preview.payable_replace_warning && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message="پرداختیِ قبلی جایگزین می‌شود" description={preview.payable_replace_warning} />
            )}
            {preview.stage === "payment" && (
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message="مرحلهٔ ۲ — فقط «پرداختی از بانک» ثبت می‌شود"
                description="حق بیمهٔ مرحلهٔ ۱ دست نمی‌خورد. پرداختیِ هر نفر خودکار در «پرداخت حقوق» ثبت می‌شود، از ماندهٔ حقوقش کم می‌شود و در «جزئیات پرداخت»ِ فیش می‌آید — حقوقِ دوره بسته می‌ماند و محاسبهٔ مجدد لازم نیست." />
            )}
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag>سطر: {preview.rows}</Tag>
              <Tag color="green">تطبیق‌یافته: {preview.matched}</Tag>
              <Tag color="orange">بدون تطبیق: {preview.unmatched}</Tag>
              {preview.ambiguous > 0 && <Tag color="red">نامِ تکراری: {preview.ambiguous}</Tag>}
              {preview.stage === "payment"
                ? <Tag color="blue">جمع پرداختی از بانک: {money(preview.total_payable)} ریال</Tag>
                : <Tag color="blue">جمع کسر (حق بیمه): {money(preview.total_deduction)} ریال</Tag>}
              <Tag>سطر عنوان: {preview.header_row}</Tag>
            </Space>
            {preview.stage !== "payment" && (
              <div style={{ marginBottom: 12 }}>
                <Checkbox checked={replace} onChange={(e) => setReplace(e.target.checked)}>
                  جایگزینیِ ردیف‌های قبلیِ این دوره (پیشنهاد می‌شود — از کسرِ دوبارهٔ بیمه جلوگیری می‌کند)
                </Checkbox>
                {!!preview.existing_rows && (
                  <div style={{ fontSize: 12, color: "var(--gx-text-sec, #888)", marginTop: 2 }}>
                    این دوره از قبل {preview.existing_rows} ردیفِ بیمه دارد. با تیک: همه پاک و همین فایل جایگزین می‌شود.
                    بی‌تیک: ردیف‌های این فایل <b>کنارِ</b> قبلی‌ها اضافه می‌شوند و بیمه دوبار کسر می‌شود.
                  </div>
                )}
              </div>
            )}
            <GxTable size="small" rowKey={(r, i) => i} pagination={{ pageSize: 8 }}
              dataSource={preview.items}
              columns={[
                { title: "نام", dataIndex: "employee_name_raw" },
                { title: "کد ملی", dataIndex: "national_id_raw" },
                ...(preview.stage === "payment"
                  ? [{ title: "پرداختی از بانک", dataIndex: "payable_info", align: "left", render: money }]
                  : [{ title: "کسر بیمه (حق بیمه)", dataIndex: "total_insurance_deduction", align: "left", render: money },
                     { title: "پرداختی (ثبت در پرداخت حقوق)", dataIndex: "payable_info", align: "left", render: money }]),
                {
                  /* v2.163.0 (کاربر): «اگر تطبیق مشکل داشت، همان‌جا موقعِ آپلود وصلش کنیم» — انتخاب روی ثبت اعمال می‌شود */
                  title: "تطبیق", dataIndex: "matched_status", width: 260,
                  render: (v, r) => {
                    const picked = Object.prototype.hasOwnProperty.call(overrides, r.row);
                    return (
                      <Space direction="vertical" size={2} style={{ width: "100%" }}>
                        {!picked && (v === "matched"
                          ? <Tag color={r.match_method === "learned" ? "cyan" : "green"}>
                              {r.employee_name}{r.match_method === "learned" ? " (تطبیقِ قبلی)" : ""}</Tag>
                          : <Tag color="orange">{r.notes || "بدون تطبیق — پرسنل را انتخاب کنید"}</Tag>)}
                        <Select size="small" showSearch allowClear style={{ width: "100%" }}
                          placeholder={v === "matched" ? "تغییرِ پرسنل…" : "انتخابِ پرسنل"}
                          value={picked ? (overrides[r.row] || undefined) : undefined}
                          filterOption={(i, o) => (o?.label || "").includes(i)}
                          options={empOptions}
                          onChange={(val) => setOverrides((o) => {
                            const n = { ...o };
                            if (val) n[r.row] = val; else delete n[r.row];
                            return n;
                          })} />
                      </Space>
                    );
                  },
                },
              ]} />
          </>
        )}
      </Modal>

      {/* افزودنِ دستی */}
      <Modal open={addOpen} title="افزودن ردیف بیمه" onCancel={() => setAddOpen(false)}
        onOk={() => form.validateFields().then(async (v) => {
          const pid = await ensurePeriod();
          createInsuranceRecord(pid, v).then(() => {
            message.success("ثبت شد"); setAddOpen(false); load();
          }).catch((e) => message.error(e?.response?.data?.detail || "ثبت ناموفق بود"));
        })} okText="ثبت" cancelText="انصراف">
        {/* v2.166.0 (کاربر): «فقط اسم و مبلغ اجباری؛ بقیه از سیستم. حق بیمه و پرداختی جدا — هرکدام وارد شد همان؛ اگر قبلاً حق بیمه‌اش
            خورده لازم نیست دوباره بزند و همان قبلی نمایش داده شود» — سرور ردیفِ موجودِ همین ماه را به‌روز می‌کند. */}
        <Form form={form} layout="vertical">
          <Form.Item name="employee_id" label="پرسنلِ بیمه‌ای" rules={[{ required: true, message: "پرسنل را انتخاب کنید" }]}>
            <Select allowClear showSearch options={insuredOptions} filterOption={filterByChildren}
              placeholder="نام یا کد — فقط پرسنلِ بیمه‌ای" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(a, b) => a.employee_id !== b.employee_id}>
            {({ getFieldValue }) => {
              const cur = rows.find((r) => r.employee_id && r.employee_id === getFieldValue("employee_id"));
              return cur ? (
                <Alert type="info" showIcon style={{ marginBottom: 12 }}
                  message="این نفر در همین ماه ردیفِ بیمه دارد — فقط مبلغی را که وارد کنید عوض می‌شود"
                  description={`حق بیمهٔ فعلی: ${money(cur.insurance_premium) || "—"} ریال • پرداختیِ فعلی: ${
                    cur.net_payable ? money(cur.net_payable) : "—"} ریال`} />
              ) : null;
            }}
          </Form.Item>
          <Form.Item name="insurance_premium" label="حق بیمه سهم کارمند (ریال)"
            help="اگر قبلاً خورده و عوض نمی‌شود، خالی بگذارید"
            dependencies={["net_payable"]}
            rules={[({ getFieldValue }) => ({ validator: (_, v) => (v || getFieldValue("net_payable")
              ? Promise.resolve() : Promise.reject(new Error("حق بیمه یا پرداختی — حداقل یکی"))) })]}>
            <MoneyInput />
          </Form.Item>
          <Form.Item name="net_payable" label="پرداختیِ بیمه — خالص پرداختی (ریال)"
            help="مبلغی که از کانالِ بیمه (بانکِ رسمی) به خودِ پرسنل پرداخت شده؛ در «پرداخت حقوق» ثبت می‌شود">
            <MoneyInput />
          </Form.Item>
          <Form.Item name="notes" label="توضیح"><Input /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
